import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import { BASE_URL_API } from "../References/urls";
import AuthContext from "./auth-context";

const ExecutiveContext = createContext();

export const ExecutiveProvider = ({ children }) => {
    const [executives, setExecutives] = useState(() => {
        // ✅ Load from localStorage initially
        const saved = localStorage.getItem("executivesData");
        return saved ? JSON.parse(saved) : [];
    });
    const [isLoading, setIsLoading] = useState(false);

    const authCtx = useContext(AuthContext);

    // ✅ Avoid calling logout inside render
    useEffect(() => {
        if (authCtx.token === "") {
            authCtx.logout();
        }
    }, [authCtx]);

    // ✅ Fetch executives list
    const adminGetAllExecutives = useCallback(async () => {
        if (!authCtx?.token) return;

        setIsLoading(true);
        try {
            const res = await fetch(`${BASE_URL_API}/adminGetAllExecutives`, {
                method: "POST",
                body: JSON.stringify({ token: authCtx.token }),
            });

            if (!res.ok) throw new Error("Server Error");

            const data = await res.json();
            if (data.status === "success") {
                const list = Array.isArray(data.response) ? data.response : [];
                setExecutives(list);
                localStorage.setItem("executivesData", JSON.stringify(list)); // ✅ persist in localStorage
            } else if (data.message === "token Expired, please login again!") {
                authCtx.logout();
            }
        } catch (err) {
            console.error("❌ Fetch executives failed:", err);
        } finally {
            setIsLoading(false);
        }
    }, [authCtx]);

    // ✅ Fetch when token available
    useEffect(() => {
        if (authCtx?.token) adminGetAllExecutives();
        else console.log("ℹ️ Not logged in yet...");
    }, [authCtx?.token, adminGetAllExecutives]);

    // ✅ Sync to localStorage whenever executives update
    useEffect(() => {
        localStorage.setItem("executivesData", JSON.stringify(executives));
    }, [executives]);

    return (
        <ExecutiveContext.Provider
            value={{
                executives,
                isLoading,
                refresh: adminGetAllExecutives,
            }}
        >
            {children}
        </ExecutiveContext.Provider>
    );
};

// ✅ Hook for use in React components
export const useExecutives = () => useContext(ExecutiveContext);

// ExecutiveContext.js

function loadExecutivesData() {
    try {
        const saved = localStorage.getItem("executivesData");
        return saved ? JSON.parse(saved) : [];
    } catch {
        return [];
    }
}

// ✅ A simple array, not a function or proxy
const executivesData = loadExecutivesData();

// Optional helper if you ever need to refresh it later manually
export function refreshExecutivesData() {
    return loadExecutivesData();
}

// Your other exports
// export { useExecutives, getExecutiveFullName };
export { executivesData };



// ✅ Utility: get full name
export const getExecutiveFullName = (userID) => {
    const data = executivesData
    const executive = data.find((exec) => exec?.userID === userID);
    if (!executive) return null;
    return [executive.firstName, executive.middleName, executive.lastName]
        .filter(Boolean)
        .join(" ");
};
